import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { TextAreaSc } from 'components/final-form/TextArea';
import _ from 'lodash';
import moment from 'moment';
import React, { KeyboardEvent, useEffect, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  CommentChangeStatus,
  PatientMedicalDocumentCommentDto,
  UpsertPatientMedicalDocumentCommentDto,
  UserDisplayDto
} from 'ts-types/api.types';
import { isoToDisplayDateTime, toIsoDateTimeString } from 'utils/dateUtils';

const CommentsDiv = styled.div`

  .display-linebreak {
    white-space: pre-line;
  }

  .new-comment {
    margin-top: 0.5rem;
  }

  .comment,
  .new-comment {
    margin-left: 1.5rem;
  }

  .comment-author {
    font-weight: bolder;
  }

  i.icon.comment-author-icon {
    margin-left: -1.5rem;
    color: #5a5a5a;
    display: block;
    position: absolute;
  }

  i.icon.comment-author-icon-deleted {
    margin-left: -1.5rem;
    color: #934242;
    display: block;
    position: absolute;
  }

  .comment-timestamp {
    margin-left: 1.2rem;
    color: #777777;
  }

  .comment-timestamp-deleted {
    margin-left: 1.2rem;
    color: #934242;
  }

  .comment-actions {
    margin-top: 0.5rem;

    button {
      margin-right: 0.5rem;
    }
  }

  .comment-deleted {
    color: #934242;
  }
`;

export const prepareComments = (comments: PatientMedicalDocumentCommentDto[])
    : Partial<PatientMedicalDocumentCommentDtoExtended>[] => {

  let result: Partial<PatientMedicalDocumentCommentDtoExtended>[] =
      comments.map(comment => ({
        ...comment,
        commentChangeStatus: 'NO_CHANGE'
      } as Partial<PatientMedicalDocumentCommentDtoExtended>));

  result = _.sortBy(result,
      comment => moment(comment.dateAdded));

  return result.reverse();
}

export const prepareUpsertCommentsDtos = (comments: Partial<PatientMedicalDocumentCommentDtoExtended>[])
    : UpsertPatientMedicalDocumentCommentDto[] => {

  const upsertComments = comments
      .filter(comment =>
          comment.commentChangeStatus && comment.commentChangeStatus !== "NO_CHANGE")
      .map(comment => ({
        id: comment.id,
        commentChangeStatus: comment.commentChangeStatus,
        dateAdded: comment.dateAdded,
        comment: comment.comment
      } as UpsertPatientMedicalDocumentCommentDto));

  return upsertComments;
}

export interface PatientMedicalDocumentCommentDtoExtended extends PatientMedicalDocumentCommentDto {
  commentChangeStatus: CommentChangeStatus;
}

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  disabled: boolean;
  readonly: boolean;
  currentUserDisplay: UserDisplayDto;
  comments: Partial<PatientMedicalDocumentCommentDtoExtended>[];
  onChange: (comments: Partial<PatientMedicalDocumentCommentDtoExtended>[]) => void;
  setAddingComment: (addingComment: boolean) => void;
  setEditingComments: (editingComments: boolean) => void;
  showDeletedComments: boolean;
}

const PatientDocumentComments = (props: Props) => {

  const textAreaRows = 3;

  const {
    readonly,
    comments,
    currentUserDisplay,
    onChange,
    setAddingComment,
    setEditingComments,
    showDeletedComments,
    getMessage
  } = props;

  const [editCommentIndex, setEditCommentIndex] = useState<number | undefined>(undefined);
  const [editComment, setEditComment] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newCommentExpended, setNewCommentExpanded] = useState(false);

  const currentUserFullName = currentUserDisplay
      ? `${currentUserDisplay.firstName} ${currentUserDisplay.name}`
      : "";

  const handleSave = (index: number, comment: string) => {
    let newComments = [...comments];
    comments[index].comment = comment;
    comments[index].commentChangeStatus = comments[index].commentChangeStatus === "NEW" ? "NEW" : "UPDATED"

    setEditComment("");
    setEditCommentIndex(undefined);

    onChange(newComments);
  };

  useEffect(() => {
    setEditingComments && setEditingComments(editCommentIndex !== undefined);
  }, [editCommentIndex]);

  useEffect(() => {
    setAddingComment && setAddingComment(!!newComment && newComment.length > 0 && newCommentExpended);
  }, [newComment, newCommentExpended]);

  const handleEdit = (comment: Partial<PatientMedicalDocumentCommentDtoExtended>, index: number) => {
    setEditCommentIndex(index);
    setEditComment(comment.comment!);
  }

  const handleDelete = (index: number) => {
    let documentComments = [...comments];

    if (documentComments[index].commentChangeStatus === "NEW") {
      documentComments.splice(index, 1);
    } else {
      let newValues: Partial<PatientMedicalDocumentCommentDtoExtended> = {
        ...documentComments[index],
        commentChangeStatus: "DELETED"
      }
      documentComments[index] = newValues;
    }

    onChange(documentComments);
  }

  const addPatientMedicalDocumentComment = () => {
    const comment = newComment;

    if (comment !== undefined && comment !== '') {


      const newComment: Partial<PatientMedicalDocumentCommentDtoExtended> = {
        commentChangeStatus: "NEW",
        dateAdded: toIsoDateTimeString(moment()),
        comment: comment
      };

      let newComments = comments ? [newComment, ...comments] : [newComment];

      onChange(newComments);
    }

    setNewComment("");
    setNewCommentExpanded(false);
  }

  const handleCancel = () => {
    setEditComment("");
    setEditCommentIndex(undefined);
  }

  const renderUserName = (comment: Partial<PatientMedicalDocumentCommentDtoExtended>) => {

    let addedByUserName = currentUserFullName;
    if (comment.commentChangeStatus !== "NEW") {
      addedByUserName = comment.addedByUserName!;
    }

    return <strong>{addedByUserName}</strong>;
  }

  const renderDateAdded = (comment: Partial<PatientMedicalDocumentCommentDtoExtended>) => {
    return isoToDisplayDateTime(comment.lastChangedTimestamp);
  }

  const renderForm = (): React.ReactNode => {

    return (
        <React.Fragment>
          {comments.map((comment, index) => {
            if (showDeletedComments) {
              if (comment.id) {
                const hasParent = comment.parentId !== null && comments.filter(c => c.id === comment.parentId)[0];
                if (hasParent) {
                  return null;
                }
              }
              return renderComment(index, comment, 0)
            } else {
              if (comment.archived || comment.commentChangeStatus === "DELETED") {
                return null;
              }
              return renderCommentForm(index, comment, 0);
            }
          })}
        </React.Fragment>)
  }

  const renderComment = (index: number, comment: Partial<PatientMedicalDocumentCommentDtoExtended>, padding: number) => {

    const hasChild = comment.id !== null &&
        comments.filter(c => (c.commentChangeStatus !== "NEW") && (c.parentId === comment.id))[0];
    const childCommentIx = hasChild
        ? comments.indexOf(comments.filter(c => c.parentId === comment.id)[0])
        : undefined;
    const childComment = childCommentIx ? comments[childCommentIx] : undefined;

    return (
        <React.Fragment>
          {renderCommentForm(index, comment, padding)}
          {hasChild && childCommentIx && childComment &&
              renderComment(childCommentIx, childComment, padding + 1.5)
          }
        </React.Fragment>
    )
  }

  const renderCommentForm = (index: number,
                             comment: Partial<PatientMedicalDocumentCommentDtoExtended>,
                             padding: number) => {

    const disableBtn = (editCommentIndex !== undefined && editCommentIndex !== index)
        || comment.archived || comment.commentChangeStatus === "DELETED";

    let classNameStr = "ui form comment";
    let timestampClassNameStr = "comment-timestamp";
    let iconClassNameStr = "comment-author-icon";
    if (showDeletedComments) {
      if (comment.commentChangeStatus === "DELETED" || comment.archived) {
        classNameStr = classNameStr + " comment-deleted";
        timestampClassNameStr = timestampClassNameStr + "-deleted";
        iconClassNameStr = iconClassNameStr + "-deleted";
      }
    }

    return (
        <div key={index} className={classNameStr} style={{paddingLeft: `${padding}rem`}}>

          <Icon className={iconClassNameStr} name="user circle"/>

          <div className="comment-header" style={{marginTop: "0.5rem"}}>
            <span className="comment-author">
              {renderUserName(comment)}
            </span>
            <span className={timestampClassNameStr}>
              {renderDateAdded(comment)}
            </span>
          </div>

          <div className="comment-body">
            {!readonly && editCommentIndex === index
                ? <>
                  <TextAreaSc
                      className="textarea"
                      rows={textAreaRows}
                      value={editComment}
                      onChange={(e: any) => setEditComment(e.target.value)}
                  />

                  <div className="comment-actions">
                    <Button
                        type="button"
                        primary
                        compact
                        size="tiny"
                        onClick={() => handleSave(editCommentIndex, editComment)}
                    >
                      {getMessage("button.save")}
                    </Button>

                    <Button
                        type="button"
                        compact
                        size="tiny"
                        onClick={() => handleCancel()}
                    >
                      {getMessage("button.cancel")}
                    </Button>
                  </div>
                </>
                :
                <>
                  <div className="comment-text display-linebreak">
                    {comment.comment}
                  </div>

                  {
                      !readonly && comment.commentChangeStatus !== "DELETED" && !comment.archived &&
                      <div className="comment-actions">
                        <EditActionButton
                            hoverMessage={getMessage("msgEdit")}
                            disabled={disableBtn}
                            onConfirm={() => handleEdit(comment, index)}
                        />
                        <DeleteActionButton
                            hoverMessage={getMessage("msgDelete")}
                            popupMessage={getMessage("patientDocument.patientMedicalDocument.confirmDelete")}
                            disabled={disableBtn}
                            onConfirm={() => handleDelete(index)}
                        />
                      </div>
                  }
                </>
            }
          </div>
        </div>
    )
  }

  const onEnterKeyPressedInAddNewComment = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey && e.keyCode === 13) {
      e.preventDefault();
      addPatientMedicalDocumentComment();
      setNewCommentExpanded(true);
    }
  };

  return (
      <CommentsDiv className="ui form">
        {
            !readonly &&
            <div className="comment new-comment">

              <Icon className="comment-author-icon" name="user circle"/>

              <div className="comment-header" style={{marginTop: "0.5rem"}}>
            <span className="comment-author">
              {currentUserFullName}
            </span>
              </div>

              <div className="comment-body">
                <TextAreaSc
                    className="textarea"
                    rows={newCommentExpended ? textAreaRows : 1}
                    value={newComment}
                    onFocus={() => setNewCommentExpanded(true)}
                    onKeyDown={onEnterKeyPressedInAddNewComment}
                    onChange={(e: any) => setNewComment(e.target.value)}
                    placeholder={getMessage("patientDocument.patientMedicalDocument.comment")}
                    disabled={editCommentIndex !== undefined}
                />
              </div>

              {
                newCommentExpended
                    ? <div className="comment-actions">
                      <Button
                          type="button"
                          primary
                          compact
                          size="tiny"
                          disabled={editCommentIndex !== undefined}
                          onClick={() => addPatientMedicalDocumentComment()}
                      >
                        {getMessage("button.save")}
                      </Button>

                      <Button
                          type="button"
                          compact
                          size="tiny"
                          onClick={() => {
                            setNewComment("");
                            setNewCommentExpanded(false);
                          }}
                      >
                        {getMessage("button.cancel")}
                      </Button>
                    </div>
                    : <></>
              }
            </div>
        }

        {renderForm()}

      </CommentsDiv>
  )
}

export default withAuthContext(withMessages(PatientDocumentComments));
